import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ExtendedAttributeFilterInterface } from 'app/library/all-users/interfaces/extended-attribute-filer.interface';
import { SimpleCertificate } from 'app/library/certificate/models/simple-certificate.model';
import { ISimpleCertificate } from 'app/library/certificate/services/certificate.api-service';
import { IModuleUserDateRangeFilters } from 'app/library/module-user/interfaces/module-user-date-range-filters.interface';
import { IModuleUserQuery } from 'app/library/module-user/interfaces/module-user.query.interface';
import { appendModuleUserDateRangeFilters } from 'app/library/module-user/utils/module-user-date-range-filters.util';
import { ModuleUserExtended } from 'app/library/module-user/models/module-user.extended.model';
import { ModuleUser } from 'app/library/module-user/models/module-user.model';
import { IEnvironment } from 'app/projects/core/src/lib/interfaces/environment.interface';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { IPaginatedResponse } from 'app/projects/subentity/src/lib/interfaces/paginated-response.interface';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { IUnbookProgramExecutionUsersActionPayload } from '../interfaces/program-execution-unbook-users-action.action-payload.interface';
import { UnbookProgramExecutionUsersCommand } from '../models/unbook-program-execution-users.command.model';
import { INotifyAllParticipantsActionPayload } from 'app/library/module/interfaces/notfiy-all.action.interface';
import { ICourseOfferingQuery } from 'app/library/course-offering/interfaces/course-offering.query.interface';
import { INotifySelectedParticipantsActionPayload } from 'app/library/module/interfaces/notfiy-selected.action.interface';
import { MaxBrainUtils } from 'app/projects/core/src/lib/utils';
import { ParticipantBooking } from '../models/participant-booking.model';
import { IParticipantBookingQuery } from '../interfaces/participant-booking.query.interface';
import { INotifyFitlersActionPayload } from 'app/library/module/interfaces/notfiy-filters.action.interface';
import { IUsersAndTotalCount } from 'app/library/all-users/services/all-users.api-service';

interface IExtendedPaginatedResponse<T> extends IPaginatedResponse<T> {
    number_of_participants: number;
}

@Injectable()
export class CourseOfferingParticipantsApiService {
    constructor(private http: HttpClient, @Inject(MAXBRAIN_ENVIRONMENT) private environment: IEnvironment) {}

    getCourseOfferingParticipants(
        courseOfferingId = '',
        search = '',
        sortBy = '',
        sortOrder = '',
        pageSize = 10,
        pageIndex = 1,
        withoutCertificate?: boolean,
        selectedCertificateIds?: string[],
        selectedStatusIds?: string[],
        selectedExecutionIds?: string[],
        dateRangeFilters?: IModuleUserDateRangeFilters,
        audienceIds?: string[],
        teamIds?: string[],
        extendedAttributesFilters?: ExtendedAttributeFilterInterface[],
        isUnassignedSelectedAudiences?: boolean,
        isUnassignedSelectedTeams?: boolean,
        excludedTeamIds?: string[],
        teamRoleIds?: string[],
        columns?: string[]
    ): Observable<IExtendedPaginatedResponse<IModuleUserQuery>> {
        let params = new HttpParams().set('page', pageIndex ? pageIndex.toString() : '1').set('limit', pageSize ? pageSize.toString() : '10');

        if (search) {
            params = params.set('filter[query]', search);
        }

        if (sortBy && sortOrder) {
            if (sortBy === 'name' || sortBy === 'fullName') {
                sortBy = 'name';
            }

            params = params.set('sortBy', `${sortBy}`);
            params = params.set('sortOrder', sortOrder);
        }

        if (withoutCertificate) {
            params = params.set('filter[withoutCertificate]', String(withoutCertificate));
        }

        if (selectedCertificateIds?.length) {
            params = params.set('filter[certificate]', selectedCertificateIds.toString());
        }

        if (selectedStatusIds?.length) {
            params = params.set('filter[completion]', selectedStatusIds.toString());
        }

        if (selectedExecutionIds?.length) {
            params = params.set('filter[execution]', selectedExecutionIds.toString());
        }

        if (dateRangeFilters) {
            params = appendModuleUserDateRangeFilters(params, dateRangeFilters);
        }

        params = this._appendBookingsUserFilters(params, {
            audienceIds,
            teamIds,
            extendedAttributesFilters,
            isUnassignedSelectedAudiences,
            isUnassignedSelectedTeams,
            excludedTeamIds,
            teamRoleIds,
            columns,
        });

        return this.http.get<IExtendedPaginatedResponse<IModuleUserQuery>>(`${this.environment.apiUrl}/course-structure/course-offerings/${courseOfferingId}/participants`, {
            params,
        });
    }

    getProgramExecutionCourseOfferingParticipants(
        courseOfferingId = '',
        programExecutionId = '',
        search = '',
        sortBy = '',
        sortOrder = '',
        pageSize = 10,
        pageIndex = 1,
        withoutCertificate?: boolean,
        selectedCertificateIds?: string[],
        selectedStatusIds?: string[],
        selectedExecutionIds?: string[],
        dateRangeFilters?: IModuleUserDateRangeFilters,
        audienceIds?: string[],
        teamIds?: string[],
        extendedAttributesFilters?: ExtendedAttributeFilterInterface[],
        isUnassignedSelectedAudiences?: boolean,
        isUnassignedSelectedTeams?: boolean,
        excludedTeamIds?: string[],
        teamRoleIds?: string[],
        columns?: string[]
    ): Observable<IExtendedPaginatedResponse<IModuleUserQuery>> {
        let params = new HttpParams().set('page', pageIndex ? pageIndex.toString() : '1').set('limit', pageSize ? pageSize.toString() : '10');

        if (search) {
            params = params.set('filter[query]', search);
        }

        if (sortBy && sortOrder) {
            if (sortBy === 'name' || sortBy === 'fullName') {
                sortBy = 'name';
            }

            params = params.set('sortBy', `${sortBy}`);
            params = params.set('sortOrder', sortOrder);
        }

        if (withoutCertificate) {
            params = params.set('filter[withoutCertificate]', String(withoutCertificate));
        }

        if (selectedCertificateIds?.length) {
            params = params.set('filter[certificate]', selectedCertificateIds.toString());
        }

        if (selectedStatusIds?.length) {
            params = params.set('filter[completion]', selectedStatusIds.toString());
        }

        if (selectedExecutionIds?.length) {
            params = params.set('filter[execution]', selectedExecutionIds.toString());
        }

        if (dateRangeFilters) {
            params = appendModuleUserDateRangeFilters(params, dateRangeFilters);
        }

        params = this._appendBookingsUserFilters(params, {
            audienceIds,
            teamIds,
            extendedAttributesFilters,
            isUnassignedSelectedAudiences,
            isUnassignedSelectedTeams,
            excludedTeamIds,
            teamRoleIds,
            columns,
        });

        return this.http.get<IExtendedPaginatedResponse<IModuleUserQuery>>(
            `${this.environment.apiUrl}/course-structure/program-executions/${programExecutionId}/course-offerings/${courseOfferingId}/participants`,
            { params }
        );
    }

    getCourseOfferingAssignedCertificates(programExecutionId: string, courseOfferingId: string): Observable<SimpleCertificate[]> {
        return this.http
            .get<ISimpleCertificate[]>(
                `${this.environment.apiUrl}/course-structure/program-executions/${programExecutionId}/course-offerings/${courseOfferingId}/assigned-certificates`
            )
            .pipe(map((queries) => queries.map((query) => new SimpleCertificate(query))));
    }

    downloadProgramExecutionCourseOfferingAllUsersList(
        programExecutionId: string,
        courseOfferingId: string,
        search: string,
        moduleIds: string[],
        certificateIds: string[],
        completionStatusIds: string[],
        isUnassignedSelected = false
    ): Observable<Blob> {
        let params = new HttpParams();

        if (search) {
            params = params.set('filter[query]', search);
        }

        if (moduleIds.length) {
            params = params.append('filter[execution]', moduleIds.join());
        }

        if (certificateIds.length) {
            params = params.append('filter[certificate]', certificateIds.join());
        }

        if (isUnassignedSelected) {
            params = params.set('filter[withoutCertificate]', 'null');
        }

        if (completionStatusIds.length) {
            params = params.append('filter[completion]', completionStatusIds.join());
        }

        return this.http
            .get(`${this.environment.apiUrl}/course-structure/program-executions/${programExecutionId}/course-offerings/${courseOfferingId}/booking-list`, {
                params,
                responseType: 'blob',
            })
            .pipe(
                tap((userListFile) => MaxBrainUtils.downloadFile(userListFile, 'bookings.xlsx')),
                catchError((error) => {
                    console.error(error);
                    return throwError('Failed to download all bookings list');
                })
            );
    }

    downloadCourseOfferingAllUsersList(
        courseOfferingId: string,
        search: string,
        moduleIds: string[],
        certificateIds: string[],
        completionStatusIds: string[],
        isUnassignedSelected = false
    ): Observable<Blob> {
        let params = new HttpParams();

        if (search) {
            params = params.set('filter[query]', search);
        }

        if (moduleIds.length) {
            params = params.append('filter[execution]', moduleIds.join());
        }

        if (certificateIds.length) {
            params = params.append('filter[certificate]', certificateIds.join());
        }

        if (isUnassignedSelected) {
            params = params.set('filter[withoutCertificate]', 'null');
        }

        if (completionStatusIds.length) {
            params = params.append('filter[completion]', completionStatusIds.join());
        }

        return this.http
            .get(`${this.environment.apiUrl}/course-structure/course-offerings/${courseOfferingId}/booking-list`, {
                params,
                responseType: 'blob',
            })
            .pipe(
                tap((userListFile) => MaxBrainUtils.downloadFile(userListFile, 'bookings.xlsx')),
                catchError((error) => {
                    console.error(error);
                    return throwError('Failed to download all bookings list');
                })
            );
    }

    getModuleParticipant(moduleId: string, userId: string): Observable<ModuleUser[]> {
        return this.http
            .get<IModuleUserQuery[]>(`${this.environment.apiUrl}/modules-users?module=${moduleId}&user=${userId}`)
            .pipe(map((queries) => queries.map((query) => new ModuleUserExtended(query))));
    }

    getParticipantBookings(programExecutionId: string, userIds: string[], context?: string): Observable<ParticipantBooking[]> {
        let params = new HttpParams();
        const data = {
            user_ids: userIds,
        };

        if (context) {
            params = params.set('context', context);
        }

        return this.http
            .post<IParticipantBookingQuery[]>(
                `${this.environment.apiUrl}/course-structure/program-executions/${programExecutionId}/participants-bookings`,
                data,
                { params }
            )
            .pipe(map((queries) => queries.map((query) => new ParticipantBooking(query))));
    }

    getManagerBookings(programExecutionId: string, userIds: string[]): Observable<ParticipantBooking[]> {
        const data = {
            user_ids: userIds,
        };

        return this.http
            .post<IParticipantBookingQuery[]>(`${this.environment.apiUrl}/course-structure/program-executions/${programExecutionId}/managers-bookings`, data)
            .pipe(map((queries) => queries.map((query) => new ParticipantBooking(query))));
    }

    unbookParticipant(payload: IUnbookProgramExecutionUsersActionPayload): Observable<void> {
        const options = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            body: new UnbookProgramExecutionUsersCommand(payload),
        };

        return this.http.delete<void>(`${this.environment.apiUrl}/course-structure/program-executions/${payload.programExecutionId}/unbook-participants`, options);
    }

    unbookManager(payload: IUnbookProgramExecutionUsersActionPayload): Observable<void> {
        const options = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            body: new UnbookProgramExecutionUsersCommand(payload),
        };

        return this.http.delete<void>(`${this.environment.apiUrl}/course-structure/program-executions/${payload.programExecutionId}/unbook-managers`, options);
    }

    notifyAllProgramExecutionCourseOffering(
        programExecutionId: string,
        courseOfferingId: string,
        data: INotifyAllParticipantsActionPayload,
        notifyFilters: INotifyFitlersActionPayload
    ): Observable<any> {
        let params = new HttpParams();

        if (notifyFilters) {
            if (notifyFilters.search) {
                params = params.set('filter[query]', notifyFilters.search);
            }

            if (notifyFilters.moduleIds.length) {
                params = params.append('filter[execution]', notifyFilters.moduleIds.join());
            }

            if (notifyFilters.certificateIds.length) {
                params = params.append('filter[certificate]', notifyFilters.certificateIds.join());
            }

            if (notifyFilters.noCertificate) {
                params = params.set('filter[withoutCertificate]', notifyFilters.noCertificate);
            }

            if (notifyFilters.completionStatusIds.length) {
                params = params.append('filter[completion]', notifyFilters.completionStatusIds.join());
            }
        }

        return this.http.post<ICourseOfferingQuery>(
            `${this.environment.apiUrl}/course-structure/program-executions/${programExecutionId}/course-offerings/${courseOfferingId}/notification`,
            data
        );
    }

    notifySelectedProgramExecutionCourseOffering(
        programExecutionId: string,
        courseOfferingId: string,
        data: INotifySelectedParticipantsActionPayload,
        notifyFilters: INotifyFitlersActionPayload
    ): Observable<any> {
        let params = new HttpParams();

        if (notifyFilters) {
            if (notifyFilters.search) {
                params = params.set('filter[query]', notifyFilters.search);
            }

            if (notifyFilters.moduleIds.length) {
                params = params.append('filter[execution]', notifyFilters.moduleIds.join());
            }

            if (notifyFilters.certificateIds.length) {
                params = params.append('filter[certificate]', notifyFilters.certificateIds.join());
            }

            if (notifyFilters.noCertificate) {
                params = params.set('filter[withoutCertificate]', notifyFilters.noCertificate);
            }

            if (notifyFilters.completionStatusIds.length) {
                params = params.append('filter[completion]', notifyFilters.completionStatusIds.join());
            }
        }

        return this.http.post<ICourseOfferingQuery>(
            `${this.environment.apiUrl}/course-structure/program-executions/${programExecutionId}/course-offerings/${courseOfferingId}/notification`,
            data
        );
    }

    notifyAllCourseOffering(courseOfferingId: string, data: INotifyAllParticipantsActionPayload, notifyFilters: INotifyFitlersActionPayload): Observable<any> {
        let params = new HttpParams();

        if (notifyFilters) {
            if (notifyFilters.search) {
                params = params.set('filter[query]', notifyFilters.search);
            }

            if (notifyFilters.moduleIds.length) {
                params = params.append('filter[execution]', notifyFilters.moduleIds.join());
            }

            if (notifyFilters.certificateIds.length) {
                params = params.append('filter[certificate]', notifyFilters.certificateIds.join());
            }

            if (notifyFilters.noCertificate) {
                params = params.set('filter[withoutCertificate]', notifyFilters.noCertificate);
            }

            if (notifyFilters.completionStatusIds.length) {
                params = params.append('filter[completion]', notifyFilters.completionStatusIds.join());
            }
        }

        return this.http.post<ICourseOfferingQuery>(`${this.environment.apiUrl}/course-structure/course-offerings/${courseOfferingId}/notification`, data, { params });
    }

    notifySelectedCourseOffering(courseOfferingId: string, data: INotifySelectedParticipantsActionPayload, notifyFilters: INotifyFitlersActionPayload): Observable<any> {
        let params = new HttpParams();

        if (notifyFilters) {
            if (notifyFilters.search) {
                params = params.set('filter[query]', notifyFilters.search);
            }

            if (notifyFilters.moduleIds.length) {
                params = params.append('filter[execution]', notifyFilters.moduleIds.join());
            }

            if (notifyFilters.certificateIds.length) {
                params = params.append('filter[certificate]', notifyFilters.certificateIds.join());
            }

            if (notifyFilters.noCertificate) {
                params = params.set('filter[withoutCertificate]', notifyFilters.noCertificate);
            }

            if (notifyFilters.completionStatusIds.length) {
                params = params.append('filter[completion]', notifyFilters.completionStatusIds.join());
            }
        }

        return this.http.post<ICourseOfferingQuery>(`${this.environment.apiUrl}/course-structure/course-offerings/${courseOfferingId}/notification`, data, { params });
    }

    private _appendBookingsUserFilters(
        params: HttpParams,
        options: {
            audienceIds?: string[];
            teamIds?: string[];
            excludedTeamIds?: string[];
            extendedAttributesFilters?: ExtendedAttributeFilterInterface[];
            isUnassignedSelectedAudiences?: boolean;
            isUnassignedSelectedTeams?: boolean;
            teamRoleIds?: string[];
            columns?: string[];
        }
    ): HttpParams {
        const {
            audienceIds,
            teamIds,
            excludedTeamIds,
            extendedAttributesFilters,
            isUnassignedSelectedAudiences,
            isUnassignedSelectedTeams,
            teamRoleIds,
            columns,
        } = options;

        if (extendedAttributesFilters?.length) {
            const attributeValues: { [key: string]: (string | number)[] } = {};

            extendedAttributesFilters.forEach((attr) => {
                if (attr.textFilters?.length) {
                    const likeValues = attr.textFilters.filter((f) => f.mode === 'like').map((f) => f.value);
                    const eqValues = attr.textFilters.filter((f) => f.mode === 'eq').map((f) => f.value);

                    if (likeValues.length) {
                        params = params.append(`filter[${attr.attributeName}][like]`, likeValues.join(','));
                    }
                    if (eqValues.length) {
                        params = params.append(`filter[${attr.attributeName}][eq]`, eqValues.join(','));
                    }
                } else if (attr.value) {
                    if (!attributeValues[attr.attributeName]) {
                        attributeValues[attr.attributeName] = [];
                    }

                    attributeValues[attr.attributeName].push(attr.value);
                } else if (attr.rangeValue1 != null && attr.rangeValue2 != null) {
                    params = params.set(`filter[${attr.attributeName}][gte]`, String(attr.rangeValue1));
                    params = params.set(`filter[${attr.attributeName}][lte]`, String(attr.rangeValue2));
                }
            });

            Object.keys(attributeValues).forEach((key) => {
                params = params.append(`filter[${key}]`, attributeValues[key].join(','));
            });
        }

        if (audienceIds?.length) {
            params = params.append('filter[audience]', audienceIds.join(','));
        }

        if (teamIds?.length) {
            params = params.append('filter[team]', teamIds.join(','));
        }

        if (excludedTeamIds?.length) {
            params = params.append('filter[excludedTeam]', excludedTeamIds.join(','));
        }

        if (teamRoleIds?.length) {
            params = params.append('filter[teamRole]', teamRoleIds.join(','));
        }

        if (columns?.length) {
            params = params.append('filter[columns]', columns.join(','));
        }

        if (isUnassignedSelectedAudiences) {
            params = params.set('filter[audience]', 'null');
        }

        if (isUnassignedSelectedTeams) {
            params = params.set('filter[team]', 'null');
        }

        return params;
    }
}
